import React, { useState, useEffect } from 'react';
import {
  Box,
  Container,
  Typography,
  Grid,
  Card,
  CardContent,
  Button,
  Chip,
  Avatar,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Divider,
  TextField,
  MenuItem,
  FormControl,
  InputLabel,
  Select,
  Pagination,
  Paper,
  IconButton,
  Tooltip,
} from '@mui/material';
import {
  TrendingUp,
  Schedule,
  EmojiEvents,
  AccountTree,
  TrackChanges,
  Star,
  Timeline,
  Add,
  ArrowBack,
  FilterList,
  CalendarToday,
} from '@mui/icons-material';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { useAuth } from '../contexts/AuthContext';
import { healthWorthService } from '../services/supabaseService';
import { weeklyLockInService } from '../services/weeklyLockInService';

const MotionCard = motion(Card);

interface ActivityItem {
  id: string;
  type: 'health_assessment' | 'weekly_progress_report' | 'health_worth_change' | 'health_tree_save' | 'weekly_lockin_created' | 'weekly_lockin_expired' | 'talent_submission';
  title: string;
  description: string;
  timestamp: Date;
  value?: string;
  metadata?: Record<string, any>;
}

const ActivityLog: React.FC = () => {
  const { user } = useAuth();
  const [activities, setActivities] = useState<ActivityItem[]>([]);
  const [filteredActivities, setFilteredActivities] = useState<ActivityItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  
  // Filter states
  const [typeFilter, setTypeFilter] = useState<string>('all');
  const [dateFilter, setDateFilter] = useState<string>('all');
  const [searchQuery, setSearchQuery] = useState<string>('');
  
  // Pagination
  const [page, setPage] = useState(1);
  const itemsPerPage = 20;

  useEffect(() => {
    if (user?.id) {
      loadAllActivities();
    }
  }, [user?.id]);

  useEffect(() => {
    applyFilters();
  }, [activities, typeFilter, dateFilter, searchQuery]);

  const loadAllActivities = async () => {
    if (!user?.id) return;
    
    try {
      setLoading(true);
      const allActivities: ActivityItem[] = [];
      
      // Load weekly lock-in history and related activities
      const lockingHistory = await weeklyLockInService.getLockingHistory(user.id, 20);
      
      for (const locking of lockingHistory) {
        // Add lock-in creation activity
        allActivities.push({
          id: `lockin-created-${locking.id}`,
          type: 'weekly_lockin_created',
          title: 'Weekly Lock-in Created',
          description: `Locked in ${locking.locked_talents?.length || 0} talents for the week`,
          timestamp: new Date(locking.created_at),
          value: `${locking.total_time_minutes} min/week`,
          metadata: {
            lockingId: locking.id,
            talentCount: locking.locked_talents?.length || 0,
            weekStart: locking.week_start_date,
            weekEnd: locking.week_end_date,
          },
        });
        
        // Add lock-in expiration activity if it has expired
        const expiresAt = new Date(locking.expires_at);
        const now = new Date();
        if (expiresAt < now && (locking.status === 'expired' || locking.status === 'completed')) {
          allActivities.push({
            id: `lockin-expired-${locking.id}`,
            type: 'weekly_lockin_expired',
            title: 'Weekly Lock-in Expired',
            description: `Weekly lock-in period ended - ${locking.status === 'completed' ? 'goals achieved' : 'time expired'}`,
            timestamp: expiresAt,
            value: locking.status === 'completed' ? 'Completed' : 'Expired',
            metadata: {
              lockingId: locking.id,
              status: locking.status,
              weekStart: locking.week_start_date,
              weekEnd: locking.week_end_date,
            },
          });
        }
        
        // Load all talent submissions for this locking
        try {
          const progressEntries = await weeklyLockInService.getProgress(locking.id);
          
          for (const entry of progressEntries) {
            const talentName = locking.locked_talents?.find(t => t.talent_id === entry.talent_id)?.talent_name || entry.talent_id;
            allActivities.push({
              id: `talent-submission-${entry.id}`,
              type: 'talent_submission',
              title: 'Talent Progress Reported',
              description: `Reported ${entry.minutes_completed} minutes for ${talentName}`,
              timestamp: new Date(entry.created_at),
              value: `${entry.minutes_completed} min`,
              metadata: {
                talentId: entry.talent_id,
                talentName,
                lockingId: locking.id,
                entryDate: entry.entry_date,
                percentage: entry.completion_percentage,
                method: entry.method,
                notes: entry.notes,
              },
            });
          }
        } catch (error) {
          console.error('Error loading progress entries for locking:', locking.id, error);
        }
      }
      
      // Load health assessments
      try {
        const assessments = await healthWorthService.getHealthAssessments(user.id);
        for (let i = 0; i < assessments.length; i++) {
          const assessment = assessments[i];
          allActivities.push({
            id: `assessment-${assessment.id}`,
            type: 'health_assessment',
            title: 'Health Worth Assessment Completed',
            description: 'Recorded new cardiovascular and metabolic health metrics',
            timestamp: new Date(assessment.created_at),
            value: formatHealthWorth(assessment.future_earning_capacity),
            metadata: {
              assessmentId: assessment.id,
              healthWorth: assessment.future_earning_capacity,
              metrics: {
                resting_heart_rate: assessment.resting_heart_rate,
                vo2_max: assessment.vo2_max,
                morning_hrv: assessment.morning_hrv,
                fasting_glucose: assessment.fasting_glucose,
                hba1c: assessment.hba1c,
              },
            },
          });
          
          // Add health worth change if we have a previous assessment
          if (i < assessments.length - 1) {
            const previousAssessment = assessments[i + 1];
            const change = assessment.future_earning_capacity - previousAssessment.future_earning_capacity;
            if (change !== 0) {
              allActivities.push({
                id: `change-${assessment.id}`,
                type: 'health_worth_change',
                title: change > 0 ? 'Health Worth Increased' : 'Health Worth Decreased',
                description: 'Future earning capacity changed from last assessment',
                timestamp: new Date(assessment.created_at),
                value: `${change > 0 ? '+' : ''}${formatHealthWorth(change)}`,
                metadata: {
                  change,
                  previousWorth: previousAssessment.future_earning_capacity,
                  currentWorth: assessment.future_earning_capacity,
                  assessmentId: assessment.id,
                },
              });
            }
          }
        }
      } catch (error) {
        console.error('Error loading health assessments:', error);
      }
      
      // Sort all activities by timestamp (most recent first)
      const sortedActivities = allActivities.sort((a, b) => b.timestamp.getTime() - a.timestamp.getTime());
      
      setActivities(sortedActivities);
    } catch (error) {
      console.error('Error loading activities:', error);
      setError('Failed to load activity history');
    } finally {
      setLoading(false);
    }
  };

  const applyFilters = () => {
    let filtered = [...activities];
    
    // Apply type filter
    if (typeFilter !== 'all') {
      filtered = filtered.filter(activity => activity.type === typeFilter);
    }
    
    // Apply date filter
    const now = new Date();
    if (dateFilter !== 'all') {
      const filterDate = new Date();
      switch (dateFilter) {
        case 'today':
          filterDate.setHours(0, 0, 0, 0);
          filtered = filtered.filter(activity => activity.timestamp >= filterDate);
          break;
        case 'week':
          filterDate.setDate(filterDate.getDate() - 7);
          filtered = filtered.filter(activity => activity.timestamp >= filterDate);
          break;
        case 'month':
          filterDate.setMonth(filterDate.getMonth() - 1);
          filtered = filtered.filter(activity => activity.timestamp >= filterDate);
          break;
      }
    }
    
    // Apply search filter
    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter(activity => 
        activity.title.toLowerCase().includes(query) ||
        activity.description.toLowerCase().includes(query) ||
        (activity.value && activity.value.toLowerCase().includes(query))
      );
    }
    
    setFilteredActivities(filtered);
    setPage(1); // Reset to first page when filters change
  };

  const formatHealthWorth = (value: number | null) => {
    if (!value) return 'Not Available';
    return `$${(value / 1000000).toFixed(1)}M`;
  };

  const getActivityIcon = (type: string) => {
    switch (type) {
      case 'health_assessment': return <Timeline sx={{ color: 'success.main' }} />;
      case 'health_worth_change': return <TrendingUp sx={{ color: 'primary.main' }} />;
      case 'weekly_progress_report': return <EmojiEvents sx={{ color: 'warning.main' }} />;
      case 'health_tree_save': return <AccountTree sx={{ color: 'info.main' }} />;
      case 'weekly_lockin_created': return <TrackChanges sx={{ color: 'success.main' }} />;
      case 'weekly_lockin_expired': return <Schedule sx={{ color: 'warning.main' }} />;
      case 'talent_submission': return <Add sx={{ color: 'success.main' }} />;
      default: return <Star sx={{ color: 'grey.500' }} />;
    }
  };

  const getActivityTypeLabel = (type: string) => {
    switch (type) {
      case 'health_assessment': return 'Health Assessment';
      case 'health_worth_change': return 'Health Worth Change';
      case 'weekly_progress_report': return 'Weekly Report';
      case 'health_tree_save': return 'Talent Tree';
      case 'weekly_lockin_created': return 'Lock-in Created';
      case 'weekly_lockin_expired': return 'Lock-in Expired';
      case 'talent_submission': return 'Talent Progress';
      default: return 'Unknown';
    }
  };

  const getActivityTypeColor = (type: string) => {
    switch (type) {
      case 'health_assessment': return 'success';
      case 'health_worth_change': return 'primary';
      case 'weekly_progress_report': return 'warning';
      case 'health_tree_save': return 'info';
      case 'weekly_lockin_created': return 'success';
      case 'weekly_lockin_expired': return 'warning';
      case 'talent_submission': return 'success';
      default: return 'default';
    }
  };

  const formatTimeAgo = (date: Date) => {
    const now = new Date();
    const diffInHours = Math.floor((now.getTime() - date.getTime()) / (1000 * 60 * 60));
    
    if (diffInHours < 1) return 'Just now';
    if (diffInHours < 24) return `${diffInHours}h ago`;
    const diffInDays = Math.floor(diffInHours / 24);
    if (diffInDays < 30) return `${diffInDays}d ago`;
    const diffInMonths = Math.floor(diffInDays / 30);
    return `${diffInMonths}mo ago`;
  };

  // Get paginated activities
  const startIndex = (page - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const paginatedActivities = filteredActivities.slice(startIndex, endIndex);
  const totalPages = Math.ceil(filteredActivities.length / itemsPerPage);

  const activityTypes = [
    { value: 'all', label: 'All Activities' },
    { value: 'health_assessment', label: 'Health Assessments' },
    { value: 'health_worth_change', label: 'Health Worth Changes' },
    { value: 'weekly_lockin_created', label: 'Lock-ins Created' },
    { value: 'weekly_lockin_expired', label: 'Lock-ins Expired' },
    { value: 'talent_submission', label: 'Talent Progress' },
  ];

  const dateFilters = [
    { value: 'all', label: 'All Time' },
    { value: 'today', label: 'Today' },
    { value: 'week', label: 'Past Week' },
    { value: 'month', label: 'Past Month' },
  ];

  if (loading) {
    return (
      <Container maxWidth="lg" sx={{ py: 4 }}>
        <Typography>Loading activity history...</Typography>
      </Container>
    );
  }

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      {/* Header */}
      <Box sx={{ mb: 4 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
          <IconButton component={Link} to="/dashboard" sx={{ color: 'primary.main' }}>
            <ArrowBack />
          </IconButton>
          <Typography variant="h3" component="h1" sx={{ fontWeight: 'bold' }}>
            Activity Log
          </Typography>
        </Box>
        <Typography variant="h6" color="text.secondary">
          Complete history of your health and fitness activities
        </Typography>
      </Box>

      {/* Filters */}
      <Paper sx={{ p: 3, mb: 4 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
          <FilterList color="primary" />
          <Typography variant="h6" sx={{ fontWeight: 600 }}>
            Filters
          </Typography>
        </Box>
        
        <Grid container spacing={3}>
          <Grid item xs={12} sm={6} md={3}>
            <TextField
              fullWidth
              label="Search activities"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Search by title, description..."
            />
          </Grid>
          
          <Grid item xs={12} sm={6} md={3}>
            <FormControl fullWidth>
              <InputLabel>Activity Type</InputLabel>
              <Select
                value={typeFilter}
                onChange={(e) => setTypeFilter(e.target.value)}
                label="Activity Type"
              >
                {activityTypes.map((type) => (
                  <MenuItem key={type.value} value={type.value}>
                    {type.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          
          <Grid item xs={12} sm={6} md={3}>
            <FormControl fullWidth>
              <InputLabel>Date Range</InputLabel>
              <Select
                value={dateFilter}
                onChange={(e) => setDateFilter(e.target.value)}
                label="Date Range"
              >
                {dateFilters.map((filter) => (
                  <MenuItem key={filter.value} value={filter.value}>
                    {filter.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          
          <Grid item xs={12} sm={6} md={3}>
            <Box sx={{ display: 'flex', alignItems: 'center', height: '100%' }}>
              <Typography variant="body2" color="text.secondary">
                {filteredActivities.length} activities found
              </Typography>
            </Box>
          </Grid>
        </Grid>
      </Paper>

      {/* Activity List */}
      {filteredActivities.length === 0 ? (
        <Paper sx={{ p: 4, textAlign: 'center' }}>
          <Typography variant="h6" color="text.secondary" sx={{ mb: 2 }}>
            No activities found
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Try adjusting your filters or check back after completing some activities.
          </Typography>
        </Paper>
      ) : (
        <>
          <List sx={{ bgcolor: 'background.paper', borderRadius: 2 }}>
            {paginatedActivities.map((activity, index) => (
              <React.Fragment key={activity.id}>
                <ListItem
                  alignItems="flex-start"
                  sx={{
                    py: 2,
                    '&:hover': {
                      bgcolor: 'action.hover',
                    },
                  }}
                >
                  <ListItemIcon sx={{ mt: 1 }}>
                    <Avatar sx={{ width: 40, height: 40, bgcolor: 'transparent' }}>
                      {getActivityIcon(activity.type)}
                    </Avatar>
                  </ListItemIcon>
                  
                  <ListItemText
                    primary={
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 1 }}>
                        <Typography variant="h6" sx={{ fontWeight: 600 }}>
                          {activity.title}
                        </Typography>
                        <Chip 
                          label={getActivityTypeLabel(activity.type)}
                          size="small"
                          color={getActivityTypeColor(activity.type) as any}
                          variant="outlined"
                        />
                        {activity.value && (
                          <Chip 
                            label={activity.value}
                            size="small"
                            sx={{ 
                              bgcolor: 'success.main', 
                              color: 'white',
                              fontWeight: 600
                            }}
                          />
                        )}
                      </Box>
                    }
                    secondary={
                      <Box>
                        <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                          {activity.description}
                        </Typography>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <CalendarToday sx={{ fontSize: 16, color: 'text.secondary' }} />
                          <Typography variant="caption" color="text.secondary">
                            {activity.timestamp.toLocaleDateString()} at {activity.timestamp.toLocaleTimeString()}
                          </Typography>
                          <Typography variant="caption" color="text.secondary" sx={{ ml: 1 }}>
                            ({formatTimeAgo(activity.timestamp)})
                          </Typography>
                        </Box>
                      </Box>
                    }
                  />
                </ListItem>
                
                {index < paginatedActivities.length - 1 && <Divider variant="inset" component="li" />}
              </React.Fragment>
            ))}
          </List>

          {/* Pagination */}
          {totalPages > 1 && (
            <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
              <Pagination
                count={totalPages}
                page={page}
                onChange={(_, newPage) => setPage(newPage)}
                color="primary"
                size="large"
              />
            </Box>
          )}
        </>
      )}
    </Container>
  );
};

export default ActivityLog; 